import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { Core, UseCases } from "@planzone/core";
import { CoreContext } from "@planzone/view-models";

const intercepteables: UseCases[] = [
  UseCases.allocateResources,
  UseCases.editAllocation,
  UseCases.createTask,
];

type InterceptorSignal = "proceed" | "abort";

type InterceptorFn = (
  useCase: UseCases,
  request: any,
) => Promise<InterceptorSignal>;

const RequestInterceptorContext = createContext<{
  registerInterceptor(interceptor: InterceptorFn): () => void;
}>({
  registerInterceptor: () => () => {},
});

function useRequestInterceptor() {
  return useContext(RequestInterceptorContext);
}

interface Props {
  children: React.ReactNode;
}

function RequestInterceptor({ children }: Props) {
  const { core, selector, eventDispatcher } = useContext(CoreContext);
  const interceptorsRef = useRef<InterceptorFn[]>([]);

  const registerInterceptor = useCallback((interceptor: InterceptorFn) => {
    interceptorsRef.current = [...interceptorsRef.current, interceptor];
    return () => {
      interceptorsRef.current = interceptorsRef.current.filter(
        (i) => i !== interceptor,
      );
    };
  }, []);

  const interceptedCore = useMemo(
    () =>
      Object.entries(core).reduce<Core>(
        (decoratedCore, [useCase, interactor]) => ({
          ...decoratedCore,
          [useCase]: intercepteables.includes(useCase as UseCases)
            ? (request: any) => {
                const interceptors = interceptorsRef.current;
                if (interceptors.length === 0) {
                  interactor(request);
                  return;
                }

                (async () => {
                  for (const interceptor of interceptors) {
                    const signal = await interceptor(
                      useCase as UseCases,
                      request,
                    );
                    if (signal === "abort") return;
                  }
                  interactor(request);
                })();
              }
            : interactor,
        }),
        {} as Core,
      ),
    [core],
  );

  return (
    <RequestInterceptorContext.Provider value={{ registerInterceptor }}>
      <CoreContext.Provider
        value={{ core: interceptedCore, selector, eventDispatcher }}
      >
        {children}
      </CoreContext.Provider>
    </RequestInterceptorContext.Provider>
  );
}

export {
  RequestInterceptor,
  RequestInterceptorContext,
  useRequestInterceptor,
  intercepteables,
};
export type { InterceptorSignal, InterceptorFn };
